import type { TFunction } from 'i18next'
import { useEffect } from 'react'
import type { Dispatch, SetStateAction } from 'react'

import type { ClientApiClient } from '@utils/clientApi'
import type { TickmillCompaniesEnum } from '@utils/companyName.utils'

const IB_CODE_VALIDATION_DEBOUNCE_MS = 1000

interface UseIbCodeValidationParams {
  isASOrSC: boolean
  isEUOrUK: boolean
  mainIbCode: string
  mainIbCodeError: string
  entity: TickmillCompaniesEnum
  apiClient: ClientApiClient
  setIsIbCodeValidating: Dispatch<SetStateAction<boolean>>
  setIBCodeError: Dispatch<SetStateAction<string | undefined>>
  setHasIBCode: Dispatch<SetStateAction<boolean>>
  setReferralCodeCollapsed: Dispatch<SetStateAction<boolean>>
  setFieldValue: (field: string, value: unknown, shouldValidate?: boolean) => Promise<any> | void
  t: TFunction
}

export const useIbCodeValidation = ({
  isASOrSC,
  isEUOrUK,
  mainIbCode,
  mainIbCodeError,
  entity,
  apiClient,
  setIsIbCodeValidating,
  setIBCodeError,
  setHasIBCode,
  setReferralCodeCollapsed,
  setFieldValue,
  t,
}: UseIbCodeValidationParams) => {
  useEffect(() => {
    if (!isASOrSC && !isEUOrUK) {
      return
    }

    let timeoutId: ReturnType<typeof setTimeout> | undefined
    let isCancelled = false

    if (!mainIbCode) {
      setIBCodeError('')
      setIsIbCodeValidating(false)
      return
    }

    if (mainIbCodeError) {
      setIsIbCodeValidating(false)
      return
    }

    setIsIbCodeValidating(true)

    timeoutId = setTimeout(() => {
      apiClient
        .validateIntroducingBrokerByCode(mainIbCode, entity)
        .then(() => {
          if (isCancelled) {
            return
          }

          setIBCodeError('')
          setHasIBCode(true)
        })
        .catch(() => {
          if (isCancelled) {
            return
          }

          setHasIBCode(false)

          if (isEUOrUK) {
            setFieldValue('mainIbCode', '', true)
            return
          }

          setIBCodeError(t('Referral Code does not exist.'))
          setReferralCodeCollapsed(true)
        })
        .finally(() => {
          if (isCancelled) {
            return
          }

          setIsIbCodeValidating(false)
        })
    }, IB_CODE_VALIDATION_DEBOUNCE_MS)

    return () => {
      isCancelled = true

      if (timeoutId) {
        clearTimeout(timeoutId)
      }

      setIsIbCodeValidating(false)
    }
  }, [
    isASOrSC,
    isEUOrUK,
    mainIbCode,
    mainIbCodeError,
    entity,
    apiClient,
    setIsIbCodeValidating,
    setIBCodeError,
    setHasIBCode,
    setReferralCodeCollapsed,
    setFieldValue,
    t,
  ])
}
